import React from 'react';
import { t } from '../i18n';
import Modal from './ui/Modal';

const SUIT = { s: '♠', h: '♥', d: '♦', c: '♣' };

/** Best → worst. Cards that make the hand vs kickers (kicker: true). */
export const HAND_RANKINGS = [
  {
    rank: 1,
    nameKey: 'hands.royalFlush',
    descKey: 'hands.royalFlushDesc',
    name: 'Royal flush',
    description: 'A to 10, all one suit',
    cards: [
      { r: 'A', s: 's' },
      { r: 'K', s: 's' },
      { r: 'Q', s: 's' },
      { r: 'J', s: 's' },
      { r: '10', s: 's' },
    ],
  },
  {
    rank: 2,
    nameKey: 'hands.straightFlush',
    descKey: 'hands.straightFlushDesc',
    name: 'Straight flush',
    description: 'Five in a row, all one suit',
    cards: [
      { r: '5', s: 'h' },
      { r: '6', s: 'h' },
      { r: '7', s: 'h' },
      { r: '8', s: 'h' },
      { r: '9', s: 'h' },
    ],
  },
  {
    rank: 3,
    nameKey: 'hands.fourKind',
    descKey: 'hands.fourKindDesc',
    name: 'Four of a kind',
    description: 'Four cards of the same rank',
    cards: [
      { r: '9', s: 'c' },
      { r: '9', s: 'd' },
      { r: '9', s: 'h' },
      { r: '9', s: 's' },
      { r: 'K', s: 'd', kicker: true },
    ],
  },
  {
    rank: 4,
    nameKey: 'hands.fullHouse',
    descKey: 'hands.fullHouseDesc',
    name: 'Full house',
    description: 'Three of a kind plus a pair',
    cards: [
      { r: 'Q', s: 's' },
      { r: 'Q', s: 'h' },
      { r: 'Q', s: 'd' },
      { r: '7', s: 'c' },
      { r: '7', s: 's' },
    ],
  },
  {
    rank: 5,
    nameKey: 'hands.flush',
    descKey: 'hands.flushDesc',
    name: 'Flush',
    description: 'Any five of one suit',
    cards: [
      { r: '2', s: 'd' },
      { r: '6', s: 'd' },
      { r: '9', s: 'd' },
      { r: 'J', s: 'd' },
      { r: 'K', s: 'd' },
    ],
  },
  {
    rank: 6,
    nameKey: 'hands.straight',
    descKey: 'hands.straightDesc',
    name: 'Straight',
    description: 'Five in a row, mixed suits',
    cards: [
      { r: '4', s: 'c' },
      { r: '5', s: 'd' },
      { r: '6', s: 's' },
      { r: '7', s: 'h' },
      { r: '8', s: 'c' },
    ],
  },
  {
    rank: 7,
    nameKey: 'hands.threeKind',
    descKey: 'hands.threeKindDesc',
    name: 'Three of a kind',
    description: 'Three cards of the same rank',
    cards: [
      { r: '8', s: 's' },
      { r: '8', s: 'h' },
      { r: '8', s: 'd' },
      { r: 'A', s: 'c', kicker: true },
      { r: '4', s: 's', kicker: true },
    ],
  },
  {
    rank: 8,
    nameKey: 'hands.twoPair',
    descKey: 'hands.twoPairDesc',
    name: 'Two pair',
    description: 'Two different pairs',
    cards: [
      { r: 'J', s: 'c' },
      { r: 'J', s: 'd' },
      { r: '4', s: 's' },
      { r: '4', s: 'h' },
      { r: 'A', s: 'd', kicker: true },
    ],
  },
  {
    rank: 9,
    nameKey: 'hands.pair',
    descKey: 'hands.pairDesc',
    name: 'Pair',
    description: 'Two cards of the same rank',
    cards: [
      { r: '10', s: 'h' },
      { r: '10', s: 's' },
      { r: 'K', s: 'c', kicker: true },
      { r: '6', s: 'd', kicker: true },
      { r: '2', s: 's', kicker: true },
    ],
  },
  {
    rank: 10,
    nameKey: 'hands.highCard',
    descKey: 'hands.highCardDesc',
    name: 'High card',
    description: 'No match, highest card plays',
    cards: [
      { r: 'A', s: 'd' },
      { r: 'J', s: 's', kicker: true },
      { r: '8', s: 'c', kicker: true },
      { r: '5', s: 'h', kicker: true },
      { r: '3', s: 'd', kicker: true },
    ],
  },
];

function MiniCard({ r, s, kicker = false }) {
  const label = `${r}${SUIT[s]}`;
  return (
    <span
      data-testid="mini-card"
      data-card={`${r}${s}`}
      data-kicker={kicker ? 'true' : 'false'}
      className={`box-border inline-flex items-center justify-center rounded-[6px] border-2 font-medium tnum leading-none ${
        kicker
          ? 'bg-cream-2 border-line text-mute/55'
          : 'bg-cream border-ink text-ink'
      }`}
      style={{
        width: 25,
        height: 30,
        fontSize: r === '10' ? 9.5 : 11,
        letterSpacing: r === '10' ? '-0.05em' : '-0.02em',
      }}
      aria-label={kicker ? `${label} kicker` : label}
    >
      {label}
    </span>
  );
}

/**
 * Static poker-hand reference. Read-only — no Firebase, no game writes.
 * Close with ✕ or tap outside. No footer button (the mock's Keep playing
 * was dropped so ten rows fit 375×548 without scrolling).
 *
 * No 1–10 rank numbers. Compact Modal body already pads 16px (`px-4`);
 * do not use a negative horizontal margin or text/cards touch the panel.
 * Descriptions wrap (2 lines is fine) so the longest copy in every locale
 * stays in the text column and cannot collide with the five mini cards.
 */
export default function HandRankingsModal({ open, onClose }) {
  return (
    <Modal open={open} onClose={onClose} title={t('hands.title')} compact zClass="z-[70]" testId="hand-rankings">
      <ol className="m-0 p-0 list-none" data-testid="hand-rankings-list">
        {HAND_RANKINGS.map((hand) => (
          <li
            key={hand.rank}
            data-testid={`hand-rank-${hand.rank}`}
            className="flex items-center gap-2 min-h-11 box-border border-b border-line first:border-t"
          >
            <div className="min-w-0 flex-1 overflow-hidden pr-0.5">
              <div
                data-testid="hand-name"
                className="text-[16px] font-medium text-ink tracking-[-0.015em] leading-none break-words"
              >
                {t(hand.nameKey)}
              </div>
              <div
                data-testid="hand-desc"
                className="text-[12px] font-medium text-mute leading-none mt-1 break-words"
              >
                {t(hand.descKey)}
              </div>
            </div>
            <div
              data-testid="hand-cards"
              className="flex items-center justify-end gap-[3px] shrink-0"
            >
              {hand.cards.map((card, i) => (
                <MiniCard key={`${hand.rank}-${i}`} {...card} />
              ))}
            </div>
          </li>
        ))}
      </ol>
    </Modal>
  );
}
